// "Behandlung abschließen" — primary entry point for invoice creation.
// Opens from AppointmentModal. The procedure of the appointment is pre-seeded
// as the first line item; quick chips + catalog search + free entry add more.
//
// Four exits:
//   1. Cancel: nothing changes
//   2. Without invoice: appointment → completed, no document
//   3. Save as draft: appointment → completed + invoice_draft + navigate to editor
//   4. Issue now: appointment → completed + invoice finalized (PDF generated)

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    X as XIcon, Plus, Trash2, Sparkles, Lock, Save,
    CircleSlash, Search, UserPlus, Check,
} from 'lucide-react';
import { apiService } from '../../../services/apiService';
import type {
    Appointment, BillingSettings, InvoiceCatalogItem, InvoiceLanguage,
    Patient, PatientListItem,
} from '../../../services/apiService';
import { useToast } from '../../../contexts/ToastContext';
import {
    fmtMoneyPlain, parseMoneyInput, statusLabel,
} from './invoiceHelpers';

const inputClass =
    'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';
const cellInput = 'w-full px-2 py-1 text-sm bg-transparent border border-transparent text-brand-text rounded focus:outline-none focus:ring-1 focus:ring-brand-primary/40 focus:border-brand-primary/40 focus:bg-brand-surface';

interface CompletionDialogProps {
    appointment: Appointment;
    onClose: () => void;
    onCompleted?: () => void;        // appointment list refresh hook
}

// Local working item — has no server-side id yet. The whole batch is sent
// atomically when the user picks one of the three "commit" exits.
interface WorkingItem {
    label: string;
    description?: string;
    quantity: number;
    unit: string;
    unit_price_cents: number;
    vat_rate: number;
    source?: 'catalog' | 'procedure' | 'free';
    source_id?: number | null;
    code?: string;
}

const CompletionDialog: React.FC<CompletionDialogProps> = ({ appointment, onClose, onCompleted }) => {
    const navigate = useNavigate();
    const { addToast } = useToast();

    const [settings, setSettings] = useState<BillingSettings | null>(null);
    const [catalogById, setCatalogById] = useState<Map<number, InvoiceCatalogItem>>(new Map());
    const [catalog, setCatalog] = useState<InvoiceCatalogItem[]>([]);
    const [patient, setPatient] = useState<Patient | null>(null);
    const [items, setItems] = useState<WorkingItem[]>([]);
    const [internalNote, setInternalNote] = useState('');
    const [loading, setLoading] = useState(true);
    const [busyAction, setBusyAction] = useState<string | null>(null);
    const [showCatalogSearch, setShowCatalogSearch] = useState(false);
    const [catalogQuery, setCatalogQuery] = useState('');

    // Patient picker: only shown when the appointment has no patient_id yet.
    // The link is persisted server-side via PUT /appointments/:id, so we
    // mirror the resulting id locally to flip the dialog from "blocked" to
    // "ready" without forcing a full reload of the parent calendar view.
    const [linkedPatientId, setLinkedPatientId] = useState<number | null>(null);
    const [showPatientPicker, setShowPatientPicker] = useState(false);
    const [patientQuery, setPatientQuery] = useState('');
    const [patientResults, setPatientResults] = useState<PatientListItem[]>([]);
    const [patientSearching, setPatientSearching] = useState(false);
    const [linkingId, setLinkingId] = useState<number | null>(null);
    const patientSearchSeq = useRef(0);

    const effectivePatientId: number | null =
        appointment.patient_id ?? linkedPatientId ?? null;

    // --------- bootstrap ----------

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);

                // Settings + catalog
                const [s, cat] = await Promise.all([
                    apiService.getBillingSettings(),
                    apiService.listInvoiceCatalog({ activeOnly: true }),
                ]);
                setSettings(s.data.data);
                const catList = cat.data.data || [];
                setCatalog(catList);
                const map = new Map<number, InvoiceCatalogItem>();
                catList.forEach(c => map.set(c.id, c));
                setCatalogById(map);

                // Patient (only if appointment carries a patient_id; legacy
                // appointments without one are linked inline via the picker.)
                if (appointment.patient_id) {
                    const pr = await apiService.getPatient(appointment.patient_id);
                    setPatient(pr.data.data);
                }

                // Seed first item from the appointment's procedure.
                const procName = appointment.procedure_name || '';
                // Try to find a catalog row with matching code, so the price
                // is auto-populated.
                const proceduralCatalog = catList.find(c =>
                    c.code && procName && c.code.toLowerCase() === procName.toLowerCase()
                ) || catList.find(c =>
                    procName && Object.values(c.name || {}).some(n => String(n).toLowerCase() === procName.toLowerCase())
                );
                const lang: InvoiceLanguage = (['de', 'es', 'en'] as InvoiceLanguage[]).includes(appointment.language as any)
                    ? (appointment.language as InvoiceLanguage)
                    : (s.data.data.defaults.language || 'es');

                const seed: WorkingItem = proceduralCatalog
                    ? {
                          source: 'catalog',
                          source_id: proceduralCatalog.id,
                          code: proceduralCatalog.code,
                          label: proceduralCatalog.name[lang] || proceduralCatalog.name.de || procName,
                          description: proceduralCatalog.description?.[lang] || '',
                          quantity: 1,
                          unit: proceduralCatalog.default_unit,
                          unit_price_cents: proceduralCatalog.default_price_cents,
                          vat_rate: proceduralCatalog.default_vat_rate,
                      }
                    : {
                          source: 'procedure',
                          label: procName || 'Behandlung',
                          quantity: 1,
                          unit: 'Sitzung',
                          unit_price_cents: 0,
                          vat_rate: s.data.data.defaults.vat_rate,
                      };
                setItems([seed]);
            } catch (e: any) {
                addToast(e?.response?.data?.message || 'Vorbereitung fehlgeschlagen', 'error');
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appointment.id]);

    // --------- patient picker ----------

    // Pre-seed the picker query from whatever identity scraps the appointment
    // carries, so the most likely match floats to the top on first open.
    const openPatientPicker = () => {
        const seed =
            (appointment.patient_name && appointment.patient_name.trim()) ||
            (appointment.patient_email && appointment.patient_email.trim()) ||
            '';
        setPatientQuery(seed);
        setShowPatientPicker(true);
    };

    // Debounced search. Sequence counter guards against out-of-order
    // responses when the user types fast.
    useEffect(() => {
        if (!showPatientPicker) return;
        const q = patientQuery.trim();
        const seq = ++patientSearchSeq.current;
        const t = setTimeout(async () => {
            try {
                setPatientSearching(true);
                const res = await apiService.listPatients({
                    search: q || undefined,
                    archived: 'false',
                    limit: 15,
                });
                if (seq === patientSearchSeq.current) {
                    setPatientResults(res.data.data || []);
                }
            } catch (e: any) {
                if (seq === patientSearchSeq.current) {
                    setPatientResults([]);
                }
            } finally {
                if (seq === patientSearchSeq.current) {
                    setPatientSearching(false);
                }
            }
        }, 200);
        return () => clearTimeout(t);
    }, [patientQuery, showPatientPicker]);

    const linkPatient = async (p: PatientListItem) => {
        try {
            setLinkingId(p.id);
            await apiService.updateAppointment(appointment.id, { patient_id: p.id });
            const pr = await apiService.getPatient(p.id);
            setPatient(pr.data.data);
            setLinkedPatientId(p.id);
            setShowPatientPicker(false);
            addToast(`Patient ${p.full_name} verknüpft`, 'success');
            // Let the parent calendar refresh too so the badge updates next time.
            onCompleted?.();
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Verknüpfung fehlgeschlagen', 'error');
        } finally {
            setLinkingId(null);
        }
    };

    // --------- derived ----------

    const language: InvoiceLanguage = useMemo(() => {
        const l = patient?.preferred_language || appointment.language || settings?.defaults.language || 'es';
        return (['de', 'es', 'en'] as InvoiceLanguage[]).includes(l as any) ? (l as InvoiceLanguage) : 'en';
    }, [patient, appointment, settings]);

    const totals = useMemo(() => {
        let net = 0, vat = 0;
        for (const it of items) {
            const lineNet = Math.round(it.quantity * it.unit_price_cents);
            const lineVat = Math.round(lineNet * (it.vat_rate / 100));
            net += lineNet;
            vat += lineVat;
        }
        return { net, vat, total: net + vat };
    }, [items]);

    const quickItems: InvoiceCatalogItem[] = useMemo(() => {
        if (!settings) return [];
        return settings.quick_action_catalog_ids
            .map(id => catalogById.get(id))
            .filter((c): c is InvoiceCatalogItem => !!c);
    }, [settings, catalogById]);

    const catalogResults = useMemo(() => {
        const q = catalogQuery.trim().toLowerCase();
        if (!q) return catalog.slice(0, 10);
        return catalog.filter(c => {
            const hay = [c.code, c.name?.de, c.name?.es, c.name?.en].filter(Boolean).join(' ').toLowerCase();
            return hay.includes(q);
        }).slice(0, 20);
    }, [catalogQuery, catalog]);

    // --------- item ops ----------

    const addFromCatalog = (cat: InvoiceCatalogItem) => {
        // If same catalog item is already in the list, bump its quantity
        // rather than creating a duplicate row — matches the "click chip
        // again to increment" affordance from the plan.
        const existingIdx = items.findIndex(i => i.source === 'catalog' && i.source_id === cat.id);
        if (existingIdx >= 0) {
            setItems(prev => prev.map((it, i) => i === existingIdx ? { ...it, quantity: it.quantity + 1 } : it));
            return;
        }
        setItems(prev => [...prev, {
            source: 'catalog',
            source_id: cat.id,
            code: cat.code,
            label: cat.name[language] || cat.name.de || cat.name.es || cat.name.en || '',
            description: cat.description?.[language] || '',
            quantity: 1,
            unit: cat.default_unit,
            unit_price_cents: cat.default_price_cents,
            vat_rate: cat.default_vat_rate,
        }]);
    };

    const addFreeItem = () => {
        setItems(prev => [...prev, {
            source: 'free',
            label: '',
            quantity: 1,
            unit: 'Stk',
            unit_price_cents: 0,
            vat_rate: settings?.defaults.vat_rate || 21,
        }]);
    };

    const updateItem = (idx: number, patch: Partial<WorkingItem>) => {
        setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
    };

    const removeItem = (idx: number) => {
        setItems(prev => prev.filter((_, i) => i !== idx));
    };

    // --------- exits ----------

    const markCompletedOnly = async (): Promise<void> => {
        await apiService.updateAppointment(appointment.id, { status: 'completed' });
    };

    const createInvoiceWithItems = async (): Promise<number> => {
        if (!effectivePatientId) {
            throw new Error('Termin ohne verknüpften Patienten — bitte zuerst Patient verknüpfen.');
        }
        const validItems = items.filter(it => it.label.trim().length > 0);
        if (validItems.length === 0) {
            throw new Error('Mindestens eine Position mit Bezeichnung erforderlich.');
        }
        const res = await apiService.createInvoice({
            patient_id: effectivePatientId,
            type: 'invoice',
            language,
            appointment_ids: [appointment.id],
            notes_header: appointment.appointment_date
                ? `Behandlung vom ${appointment.appointment_date.split('-').reverse().join('.')}.`
                : '',
            items: validItems.map(it => ({
                source: it.source,
                source_id: it.source_id,
                code: it.code,
                label: it.label,
                description: it.description,
                quantity: it.quantity,
                unit: it.unit,
                unit_price_cents: it.unit_price_cents,
                vat_rate: it.vat_rate,
            })),
        });
        const id = res.data.data.id;
        if (internalNote.trim()) {
            await apiService.updateInvoice(id, { internal_note: internalNote });
        }
        return id;
    };

    const onExitWithoutInvoice = async () => {
        try {
            setBusyAction('no-invoice');
            await markCompletedOnly();
            addToast('Termin abgeschlossen (ohne Rechnung)', 'success');
            onCompleted?.();
            onClose();
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Aktion fehlgeschlagen', 'error');
        } finally {
            setBusyAction(null);
        }
    };

    const onExitAsDraft = async () => {
        try {
            setBusyAction('draft');
            const invId = await createInvoiceWithItems();
            await markCompletedOnly();
            addToast('Rechnungsentwurf erstellt', 'success');
            onCompleted?.();
            onClose();
            navigate(`/admin/invoices/${invId}`);
        } catch (e: any) {
            addToast(e?.response?.data?.message || e.message || 'Aktion fehlgeschlagen', 'error');
        } finally {
            setBusyAction(null);
        }
    };

    const onExitIssueNow = async () => {
        try {
            setBusyAction('issue');
            const invId = await createInvoiceWithItems();
            const res = await apiService.finalizeInvoice(invId);
            await markCompletedOnly();
            addToast(`Rechnung ${res.data.data.number} ausgestellt`, 'success');
            onCompleted?.();
            onClose();
            navigate(`/admin/invoices/${invId}`);
        } catch (e: any) {
            addToast(e?.response?.data?.message || e.message || 'Aktion fehlgeschlagen', 'error');
        } finally {
            setBusyAction(null);
        }
    };

    // --------- render ----------

    const disabledAll = busyAction !== null || loading;

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 pb-12 px-4 bg-black/50 overflow-y-auto" onClick={onClose}>
            <div
                className="w-full max-w-3xl rounded-xl bg-brand-surface border border-brand-border shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-5 py-3 border-b border-brand-border flex items-center justify-between">
                    <div className="min-w-0">
                        <h3 className="text-base font-semibold text-brand-text truncate">
                            Behandlung abschließen
                        </h3>
                        <p className="text-xs text-brand-text-muted truncate">
                            {appointment.patient_name} ·{' '}
                            {appointment.appointment_date?.split('-').reverse().join('.')} · {appointment.start_time?.slice(0, 5)}
                            {' · '}
                            {appointment.procedure_name}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 text-brand-text-muted hover:text-brand-text rounded cursor-pointer"
                        aria-label="Schließen"
                    >
                        <XIcon className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                </div>

                {loading ? (
                    <div className="p-12 text-center text-sm text-brand-text-muted">Lade…</div>
                ) : (
                    <>
                        {/* Body */}
                        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">

                            {/* Patient link banner — only when no patient is associated yet.
                                Provides a direct way to fix the precondition for invoice
                                creation instead of leaving the user stuck. */}
                            {!effectivePatientId && (
                                <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900 space-y-3">
                                    <div>
                                        <div className="font-semibold mb-1">Kein Patient verknüpft</div>
                                        <div className="text-amber-900/90">
                                            Damit eine Rechnung erstellt werden kann, muss der Termin einem Patientendatensatz zugeordnet werden.
                                            Alternativ kannst du den Termin ohne Rechnung abschließen.
                                        </div>
                                    </div>

                                    {!showPatientPicker ? (
                                        <button
                                            type="button"
                                            onClick={openPatientPicker}
                                            disabled={disabledAll}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-md transition-colors cursor-pointer disabled:opacity-50"
                                        >
                                            <UserPlus className="h-3.5 w-3.5" strokeWidth={2} />
                                            Patient verknüpfen
                                        </button>
                                    ) : (
                                        <div className="rounded-md border border-amber-300 bg-white p-3 space-y-2">
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="relative flex-1">
                                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-text-muted" strokeWidth={1.75} />
                                                    <input
                                                        type="text"
                                                        autoFocus
                                                        value={patientQuery}
                                                        onChange={(e) => setPatientQuery(e.target.value)}
                                                        placeholder="Name, E-Mail, Telefon oder Patientennummer…"
                                                        className={inputClass + ' pl-7'}
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPatientPicker(false)}
                                                    className="px-2 py-1 text-xs text-brand-text-muted hover:bg-brand-surface-2 rounded cursor-pointer"
                                                >
                                                    Abbrechen
                                                </button>
                                            </div>
                                            <ul className="max-h-56 overflow-y-auto divide-y divide-brand-border">
                                                {patientSearching ? (
                                                    <li className="text-xs text-brand-text-muted px-2 py-2">Suche…</li>
                                                ) : patientResults.length === 0 ? (
                                                    <li className="text-xs text-brand-text-muted px-2 py-2">Keine Treffer.</li>
                                                ) : (
                                                    patientResults.map(p => (
                                                        <li key={p.id}>
                                                            <button
                                                                type="button"
                                                                onClick={() => linkPatient(p)}
                                                                disabled={linkingId !== null}
                                                                className="w-full text-left px-2 py-1.5 hover:bg-brand-surface-2 rounded transition-colors cursor-pointer flex items-center justify-between gap-2 disabled:opacity-50"
                                                            >
                                                                <div className="min-w-0">
                                                                    <div className="text-sm text-brand-text truncate">
                                                                        {p.full_name}
                                                                        {p.patient_number && (
                                                                            <span className="ml-2 text-xs text-brand-text-muted">#{p.patient_number}</span>
                                                                        )}
                                                                    </div>
                                                                    <div className="text-xs text-brand-text-muted truncate">
                                                                        {[p.email, p.phone_primary].filter(Boolean).join(' · ') || '—'}
                                                                    </div>
                                                                </div>
                                                                {linkingId === p.id ? (
                                                                    <span className="text-xs text-brand-text-muted shrink-0">Verknüpfe…</span>
                                                                ) : (
                                                                    <Check className="h-3.5 w-3.5 text-brand-primary shrink-0" strokeWidth={2} />
                                                                )}
                                                            </button>
                                                        </li>
                                                    ))
                                                )}
                                            </ul>
                                            <div className="text-[11px] text-brand-text-muted">
                                                Patient nicht dabei?{' '}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        onClose();
                                                        navigate('/admin/patients');
                                                    }}
                                                    className="underline hover:text-brand-primary cursor-pointer"
                                                >
                                                    In der Patientenakte anlegen
                                                </button>
                                                {' '}und danach erneut abschließen.
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Items table */}
                            <section>
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-text-muted">
                                        Erbrachte Leistungen ({items.length})
                                    </h4>
                                </div>

                                {items.length === 0 ? (
                                    <div className="rounded-md border border-dashed border-brand-border bg-brand-surface-2/40 p-4 text-sm text-brand-text-muted text-center">
                                        Noch keine Position. Wähle unten aus dem Katalog oder lege eine freie Position an.
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto rounded-md border border-brand-border">
                                        <table className="min-w-full">
                                            <thead className="bg-brand-surface-2/60 text-[11px] font-semibold uppercase tracking-wider text-brand-text-muted">
                                                <tr>
                                                    <th className="px-2 py-2 text-left">Bezeichnung</th>
                                                    <th className="px-2 py-2 text-right w-16">Mng.</th>
                                                    <th className="px-2 py-2 text-right w-24">Einzelpreis</th>
                                                    <th className="px-2 py-2 text-right w-14">MwSt%</th>
                                                    <th className="px-2 py-2 text-right w-24">Gesamt</th>
                                                    <th className="px-2 py-2 w-8" />
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-brand-border">
                                                {items.map((it, idx) => (
                                                    <CompletionRow
                                                        key={idx}
                                                        item={it}
                                                        onChange={(patch) => updateItem(idx, patch)}
                                                        onRemove={() => removeItem(idx)}
                                                    />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </section>

                            {/* Quick chips + actions */}
                            <section>
                                {quickItems.length > 0 ? (
                                    <>
                                        <div className="text-[11px] font-semibold uppercase tracking-wider text-brand-text-muted mb-2">
                                            Häufige Leistungen (1 Klick = hinzufügen)
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {quickItems.map(c => {
                                                const name = c.name[language] || c.name.de || c.name.es || c.name.en;
                                                return (
                                                    <button
                                                        key={c.id}
                                                        type="button"
                                                        onClick={() => addFromCatalog(c)}
                                                        disabled={disabledAll}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-text bg-brand-surface-2 hover:bg-brand-accent/10 hover:text-brand-primary border border-brand-border rounded-full transition-colors cursor-pointer disabled:opacity-50"
                                                    >
                                                        <Sparkles className="h-3 w-3 text-brand-accent" strokeWidth={2} />
                                                        <span>{name}</span>
                                                        <span className="text-brand-text-muted tabular-nums">{fmtMoneyPlain(c.default_price_cents)} €</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-xs text-brand-text-muted italic">
                                        Tipp: In Einstellungen → Rechnungsstellung → Schnellauswahl lassen sich bis zu 8 häufige Leistungen als 1-Klick-Buttons hinterlegen.
                                    </div>
                                )}

                                <div className="mt-3 flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowCatalogSearch(v => !v)}
                                        disabled={disabledAll}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-primary border border-brand-border hover:bg-brand-surface-2 rounded-md transition-colors cursor-pointer disabled:opacity-50"
                                    >
                                        <Search className="h-3.5 w-3.5" strokeWidth={1.75} />
                                        Aus Katalog suchen
                                    </button>
                                    <button
                                        type="button"
                                        onClick={addFreeItem}
                                        disabled={disabledAll}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-primary border border-brand-border hover:bg-brand-surface-2 rounded-md transition-colors cursor-pointer disabled:opacity-50"
                                    >
                                        <Plus className="h-3.5 w-3.5" strokeWidth={2} />
                                        Freie Position
                                    </button>
                                </div>

                                {showCatalogSearch && (
                                    <div className="mt-3 rounded-md border border-brand-border bg-brand-surface-2/40 p-3">
                                        <input
                                            type="text"
                                            autoFocus
                                            value={catalogQuery}
                                            onChange={(e) => setCatalogQuery(e.target.value)}
                                            placeholder="Code oder Bezeichnung…"
                                            className={inputClass + ' mb-2'}
                                        />
                                        <ul className="max-h-48 overflow-y-auto divide-y divide-brand-border">
                                            {catalogResults.length === 0 ? (
                                                <li className="text-xs text-brand-text-muted px-2 py-2">Keine Treffer.</li>
                                            ) : (
                                                catalogResults.map(c => {
                                                    const name = c.name[language] || c.name.de || c.name.es || c.name.en;
                                                    return (
                                                        <li key={c.id}>
                                                            <button
                                                                type="button"
                                                                onClick={() => addFromCatalog(c)}
                                                                className="w-full text-left px-2 py-1.5 hover:bg-brand-surface-2 rounded transition-colors cursor-pointer flex items-center justify-between gap-2"
                                                            >
                                                                <span className="text-sm text-brand-text truncate">{name}</span>
                                                                <span className="text-xs text-brand-text-muted tabular-nums shrink-0">{fmtMoneyPlain(c.default_price_cents)} €</span>
                                                            </button>
                                                        </li>
                                                    );
                                                })
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </section>

                            {/* Internal note */}
                            <section>
                                <label className="block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1">
                                    Notiz (intern, nicht auf Rechnung sichtbar)
                                </label>
                                <textarea
                                    rows={2}
                                    value={internalNote}
                                    onChange={(e) => setInternalNote(e.target.value)}
                                    placeholder="z. B. Patient wünscht Anpassung der Krone in 4 Wochen"
                                    className={inputClass}
                                />
                            </section>
                        </div>

                        {/* Totals row */}
                        <div className="px-5 py-3 border-t border-brand-border bg-brand-surface-2/40 flex items-center justify-between text-sm">
                            <div className="text-brand-text-muted text-xs">
                                Sprache: <span className="font-medium text-brand-text uppercase">{language}</span>
                                {' · '}Status Termin: {statusLabel(appointment.status as any) || appointment.status}
                            </div>
                            <div className="flex items-baseline gap-4">
                                <div className="text-xs text-brand-text-muted">
                                    Netto <span className="tabular-nums font-medium text-brand-text">{fmtMoneyPlain(totals.net)} €</span>
                                </div>
                                <div className="text-xs text-brand-text-muted">
                                    MwSt <span className="tabular-nums font-medium text-brand-text">{fmtMoneyPlain(totals.vat)} €</span>
                                </div>
                                <div className="text-base font-semibold text-brand-primary tabular-nums">
                                    {fmtMoneyPlain(totals.total)} €
                                </div>
                            </div>
                        </div>

                        {/* Action footer */}
                        <div className="px-5 py-3 border-t border-brand-border flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={disabledAll}
                                className="px-3 py-1.5 text-xs font-medium text-brand-text-muted hover:bg-brand-surface-2 rounded transition-colors cursor-pointer disabled:opacity-50"
                            >
                                Abbrechen
                            </button>
                            <button
                                type="button"
                                onClick={onExitWithoutInvoice}
                                disabled={disabledAll}
                                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-text border border-brand-border hover:bg-brand-surface-2 rounded-md transition-colors cursor-pointer disabled:opacity-50"
                            >
                                <CircleSlash className="h-3.5 w-3.5" strokeWidth={1.75} />
                                Ohne Rechnung
                            </button>
                            <button
                                type="button"
                                onClick={onExitAsDraft}
                                disabled={disabledAll || !effectivePatientId}
                                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-primary border border-brand-primary hover:bg-brand-accent/10 rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                title={!effectivePatientId ? 'Patient muss verknüpft sein' : ''}
                            >
                                <Save className="h-3.5 w-3.5" strokeWidth={2} />
                                {busyAction === 'draft' ? 'Speichere…' : 'Als Entwurf'}
                            </button>
                            <button
                                type="button"
                                onClick={onExitIssueNow}
                                disabled={disabledAll || !effectivePatientId || items.filter(it => it.label.trim().length > 0).length === 0}
                                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                title={!effectivePatientId
                                    ? 'Patient muss verknüpft sein'
                                    : items.filter(it => it.label.trim().length > 0).length === 0
                                        ? 'Mindestens eine Position mit Bezeichnung erforderlich'
                                        : ''}
                            >
                                <Lock className="h-3.5 w-3.5" strokeWidth={2} />
                                {busyAction === 'issue' ? 'Stelle aus…' : 'Direkt ausstellen'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// ====================================================================

interface CompletionRowProps {
    item: WorkingItem;
    onChange: (patch: Partial<WorkingItem>) => void;
    onRemove: () => void;
}

const CompletionRow: React.FC<CompletionRowProps> = ({ item, onChange, onRemove }) => {
    const [priceText, setPriceText] = useState(fmtMoneyPlain(item.unit_price_cents));
    useEffect(() => { setPriceText(fmtMoneyPlain(item.unit_price_cents)); }, [item.unit_price_cents]);

    const lineTotal = Math.round(item.quantity * item.unit_price_cents * (1 + item.vat_rate / 100));

    return (
        <tr className="hover:bg-brand-surface-2/30">
            <td className="px-2 py-1">
                <input
                    type="text"
                    value={item.label}
                    onChange={(e) => onChange({ label: e.target.value })}
                    placeholder="Leistung"
                    className={cellInput + ' font-medium'}
                />
            </td>
            <td className="px-2 py-1">
                <input
                    type="number"
                    step="0.5"
                    min={0}
                    value={item.quantity}
                    onChange={(e) => onChange({ quantity: Number(e.target.value) || 0 })}
                    className={cellInput + ' text-right tabular-nums'}
                />
            </td>
            <td className="px-2 py-1">
                <input
                    type="text"
                    inputMode="decimal"
                    value={priceText}
                    onChange={(e) => setPriceText(e.target.value)}
                    onBlur={() => {
                        const cents = parseMoneyInput(priceText);
                        if (cents !== item.unit_price_cents) onChange({ unit_price_cents: cents });
                        else setPriceText(fmtMoneyPlain(item.unit_price_cents));
                    }}
                    className={cellInput + ' text-right tabular-nums'}
                />
            </td>
            <td className="px-2 py-1">
                <input
                    type="number"
                    min={0} max={100}
                    value={item.vat_rate}
                    onChange={(e) => onChange({ vat_rate: Number(e.target.value) || 0 })}
                    className={cellInput + ' text-right tabular-nums'}
                />
            </td>
            <td className="px-2 py-1 text-right text-sm tabular-nums font-medium text-brand-text">
                {fmtMoneyPlain(lineTotal)}
            </td>
            <td className="px-2 py-1">
                <button
                    type="button"
                    onClick={onRemove}
                    className="p-1 text-rose-600 hover:bg-rose-50 rounded cursor-pointer"
                    aria-label="Position entfernen"
                >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                </button>
            </td>
        </tr>
    );
};

export default CompletionDialog;
